import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { Footer, Nav } from "../components/lidoo/chrome";
import { ClubCard } from "../components/lidoo/club-card";
import { Prose } from "../components/lidoo/prose";
import { getPost } from "../lib/api/lidoo.functions";
import { useI18n } from "../lib/i18n";
import { hreflangLinks } from "../lib/seo";

export const Route = createFileRoute("/guides/$slug")({
  loader: async ({ params }) => {
    const res = await getPost({ data: { slug: params.slug, lang: "en" } });
    if (!res.post) throw notFound();
    return res;
  },
  head: (ctx) => {
    const post = ctx.loaderData?.post;
    return {
      meta: [
        { title: post ? `${post.title} | sunbedly` : "Guide | sunbedly" },
        { name: "description", content: post?.excerpt ?? "" },
        ...(post ? [{ property: "og:type", content: "article" }] : []),
      ],
      links: hreflangLinks(`/guides/${ctx.params.slug}`),
    };
  },
  component: () => {
    const { post, clubs } = Route.useLoaderData();
    return <GuidePage post={post} clubs={clubs} />;
  },
});

type Post = NonNullable<Awaited<ReturnType<typeof getPost>>["post"]>;
type Clubs = Awaited<ReturnType<typeof getPost>>["clubs"];

// Presentational, so the /nl/guides/..., /it/guides/... routes render the same
// article from their own loader without duplicating any markup.
export function GuidePage({ post, clubs }: { post: Post | null; clubs: Clubs }) {
  const { t } = useI18n();

  // The loader already throws notFound() when there is no post; this guard is
  // only here so the component types don't have to pretend otherwise.
  if (!post) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.published_at,
    dateModified: post.updated_at,
    author: { "@type": "Organization", name: "sunbedly", url: "https://sunbedly.com" },
    publisher: { "@id": "https://sunbedly.com/#org" },
    mainEntityOfPage: `https://sunbedly.com/guides/${post.slug}`,
  };

  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Nav />
      <main className="mx-auto max-w-6xl px-5 pb-24">
        <div className="pt-10">
          <Link to="/guides" className="li-mono text-[12px] text-[#5B6472] hover:text-[#2C4FD6]">
            ← {t("guides_all")}
          </Link>
        </div>

        <article className="mt-6 max-w-[72ch]">
          <p className="li-mono text-[11px] uppercase tracking-widest text-[#2C4FD6]">
            {post.region_key?.replace("-", " ")}
          </p>
          <h1 className="li-serif mt-3 text-4xl leading-[1.05] text-[#1C2430] md:text-6xl">
            {post.title}
          </h1>
          <p className="li-mono mt-4 text-[11px] text-[#5B6472]">
            {t("guides_updated")}: {post.updated_at}
          </p>
          <div className="mt-8">
            <Prose markdown={post.body} />
          </div>
        </article>

        {/* The clubs the article ranks, as real cards with live prices. The
            prose can go stale; these stay in step with the database. */}
        {clubs.length > 0 ? (
          <section className="mt-16 border-t border-[#1C2430]/15 pt-10">
            <h2 className="li-serif text-2xl text-[#1C2430] md:text-3xl">
              {t("guides_clubs_in_piece")}
            </h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {clubs.map((c) => (
                <ClubCard key={c.slug} club={c} />
              ))}
            </div>
          </section>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
